import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Grid,
  Box,
  Divider,
  Typography,
  Alert,
} from '@mui/material';
import { fetchAllUsers } from '@/services/userService';
import CloseIcon from '@mui/icons-material/Close';
import { Editor } from '@tinymce/tinymce-react';
import {
  updateBlogPost,
  updateBlogMainImage,
} from '../../../services/blogService';

const DetailedBlogInfo = ({ blogInfo, onSave, closeModal, onUpdate }) => {
  const [formData, setFormData] = useState({
    post_id: '',
    post_keywords: '',
    post_title: '',
    post_img: [],
    post_detail: '',
    post_url: '',
    author_id: '',
    type_id: '',
    state_id: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (blogInfo) {
      let parsedPostImg = [];

      if (blogInfo.post_img) {
        if (Array.isArray(blogInfo.post_img)) {
          parsedPostImg = blogInfo.post_img;
        } else if (typeof blogInfo.post_img === 'string') {
          try {
            parsedPostImg = JSON.parse(blogInfo.post_img);
          } catch (error) {
            console.error('Error al parsear post_img:', error);
            parsedPostImg = [];
          }
        }
      }

      setFormData({
        post_id: blogInfo.post_id || 1,
        post_keywords: blogInfo.post_keywords || '',
        post_title: blogInfo.post_title || '',
        post_img: parsedPostImg,
        post_detail: blogInfo.post_detail || '',
        post_url: blogInfo.post_url || '',
        author_id: blogInfo.author_id || '',
        type_id: blogInfo.type_id || '',
        state_id: blogInfo.state_id,
      });
    }
  }, [blogInfo]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchAllUsers();
        if (Array.isArray(response)) {
          setUsers(response);
        }
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
      }
    };

    fetchUsers();
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
            ? 1
            : 0
          : ['type_id', 'author_id'].includes(name)
          ? Number(value) || 0
          : value,
    }));
  };

  const handleMainImageChange = e => {
    const file = e.target.files[0]; // Solo una imagen principal
    if (file) {
      setFormData(prev => ({
        ...prev,
        post_img: [file], // Guardar como array con un solo elemento
      }));
    }
  };

  const updateImageUrlsInDB = async newUrls => {
    try {
      setIsLoading(true);

      const updatedBlogData = {
        post_id: formData.post_id,
        post_img: JSON.stringify(newUrls),
      };

      const blogResponse = await updateBlogPost(updatedBlogData);

      if (blogResponse?.message?.includes('updated successfully')) {
        setSuccessMessage('Su blog fue actualizado correctamente');
        setFormData(prev => ({ ...prev, post_img: newUrls }));
      } else {
        console.error('Error al actualizar imágen:', blogResponse);
      }
    } catch (error) {
      console.error('Error en la actualización de imágen:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveImage = async index => {
    const newUrls = formData.post_img.filter((_, i) => i !== index); // Eliminar la imagen del array
    setFormData(prev => ({ ...prev, post_img: newUrls })); // Actualizar el estado
    await updateImageUrlsInDB(newUrls);
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      if (!formData.post_id || !formData.type_id) {
        console.error('Error: Falta algún campo obligatorio.');
        setIsLoading(false);
        return;
      }

      // **División de datos**
      const blogdata = {
        post_keywords: formData.post_keywords,
        post_title: formData.post_title,
        post_img: formData.post_img,
        post_detail: formData.post_detail,
        post_url: formData.post_url,
        author_id: formData.author_id,
        type_id: formData.type_id,
        state_id: formData.state_id,
      };

      // **Actualización en paralelo**
      const blogResponse = await updateBlogPost({
        id: formData.post_id,
        blogData: blogdata,
      });

      // **Validación de respuestas**
      if (blogResponse?.message?.includes('updated successfully')) {
        console.log('Actualización exitosa');
        let successMsg = 'Su blog fue actualizado correctamente';

        // **Subir imagen principal si hay una nueva**
        if (
          formData.post_img.length > 0 &&
          formData.post_img[0] instanceof File
        ) {
          console.log('Subiendo imagen principal:', formData.post_img);
          await updateBlogMainImage(blogInfo.post_id, formData.post_img);
        }

        if (typeof onSave === 'function') {
          onSave(formData);
        }
        setSuccessMessage(successMsg);
      } else {
        console.error('Error en la actualización:', blogResponse);
      }
    } catch (error) {
      console.error('Error en la actualización:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={!!blogInfo} onClose={closeModal} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {'Modificar '}
        <IconButton onClick={closeModal} sx={{ color: 'grey.500' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Typography variant="h6">Información del Blog</Typography>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Post Title"
              name="post_title"
              value={formData.post_title}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid item xs={12} sx={{ mb: 2, mt: 2 }}>
              <Typography variant="h8">Post Details</Typography>
              <Divider />
            </Grid>
            <Editor
              apiKey="7qgbz8mytlvs2ilwkv3944y4ihr7ckmi04d4fvn73fbpxqfn"
              init={{
                plugins: [
                  /*
                    'anchor',
                    'autolink',
                    'charmap',
                    'codesample',
                    'emoticons',
                    'image',
                    'link',
                    'lists',
                    'media',
                    'searchreplace',
                    'table',
                    'visualblocks',
                    'wordcount',
                    'checklist',
                    'mediaembed',
                    'casechange',
                    'export',
                    'formatpainter',
                    'pageembed',
                    'a11ychecker',
                    'permanentpen',
                    'powerpaste',
                    'advtable',
                    'advcode',
                    'editimage',
                    'advtemplate',
                    'mentions',
                    'tinycomments',
                    'tableofcontents',
                    'footnotes',
                    'mergetags',
                    'autocorrect',
                    'typography',
                    'inlinecss',
                    'markdown',
                    'importword',
                    'exportword',
                    'exportpdf',
                    */
                ], // Eliminamos 'tinymcespellchecker'
                toolbar:
                  'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | addcomment showcomments | a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
                tinycomments_mode: 'embedded',
                tinycomments_author: 'Author name',
                menubar: true,
                readonly: false,
                branding: false, // Oculta el logo de TinyMCE
                promotion: false, // Desactiva promociones de TinyMCE
                setup: function (editor) {
                  editor.on('init', function () {
                    editor
                      .getContainer()
                      .querySelector('.tox-statusbar').style.display = 'none'; // Oculta la barra de estado
                  });
                },
              }}
              value={formData.post_detail || ''}
              onEditorChange={content => {
                setFormData(prev => ({
                  ...prev,
                  post_detail: content,
                }));
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Post Keywords"
              name="post_keywords"
              value={formData.post_keywords}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Autor"
              name="author_id"
              value={formData.author_id}
              onChange={handleChange}
              SelectProps={{ native: true }}
            >
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.user_name} {user.user_lastname}
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            {/* Subir Imagen Principal */}
            <Button variant="contained" component="label" color="primary">
              Cargar Imagen Principal
              <input
                type="file"
                hidden
                onChange={handleMainImageChange}
                accept="image/*"
              />
            </Button>

            {/* Vista previa de la imagen principal actual */}
            {formData.post_img.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, marginTop: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <img
                    src={
                      formData.post_img[0] instanceof File
                        ? URL.createObjectURL(formData.post_img[0])
                        : formData.post_img[0]
                    }
                    alt="main"
                    width={100}
                    height={100}
                    style={{ borderRadius: 8, objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFormData(prev => ({ ...prev, post_img: [] }))
                    }
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.5)',
                      color: '#fff',
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Post URL Video"
              name="post_url"
              value={formData.post_url}
              onChange={handleChange}
            />
          </Grid>

          {/* Detalles en Inglés */}

          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Post Type"
              name="type_id"
              value={formData.type_id}
              onChange={handleChange}
              SelectProps={{ native: true }}
            >
              <option value="">Selecciona un subtipo</option>
              <option value={1}>Anfitrión</option>
              <option value={2}>Espacios</option>
              <option value={3}>Invitados</option>
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Post State"
              name="state_id"
              value={formData.state_id}
              onChange={handleChange}
              SelectProps={{ native: true }}
            >
              <option value={1}>Available</option>
              <option value={2}>Unavailable</option>
              <option value={3}>Deleted</option>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      {successMessage && (
        <Box sx={{ marginTop: '20px', textAlign: 'center' }}>
          <Alert severity={'success'}>{successMessage}</Alert>
        </Box>
      )}

      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailedBlogInfo;
